import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface RangeOpt { label: string; days: number | null; }

/** BO UI Standard §17 canonical chip set for Tracking forms — 3M is the default. */
export const TRACKING_RANGES: RangeOpt[] = [
  { label: '1M', days: 30 }, { label: '3M', days: 90 }, { label: '6M', days: 180 },
  { label: '1Y', days: 365 }, { label: 'All', days: null },
];

interface Props {
  ranges: RangeOpt[];
  value: number | null;
  onChange: (days: number | null) => void;
  /** 'dropdown' (default) = the compact popup; 'chips' = the §17 Tracking-form
   *  chip row (1M · 3M · 6M · 1Y · All, active = theme orange, applies on click). */
  variant?: 'dropdown' | 'chips';
  /** Optional inline label rendered before the chips (e.g. "Date Range"). Only the
   *  chip variant uses it — a self-contained, aligned row (no external wrapper). */
  label?: string;
}

/**
 * Custom Date Range dropdown with an ORANGE selection/hover list. A native
 * <select> can't restyle its option highlight (it showed browser-blue), so this
 * renders its own popup. Picking a value only updates the draft — the caller
 * commits it on Retrieve / OK, so selecting a range does not auto-fetch.
 */
export function DateRangeSelect({ ranges, value, onChange, variant = 'dropdown', label }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const current = ranges.find((r) => r.days === value) ?? ranges[ranges.length - 1];

  // §17 chip row for Tracking forms — active chip = theme orange, applies on click.
  if (variant === 'chips') {
    return (
      <div className="dr-chips" role="group" aria-label="Date range">
        {label && <span className="dr-chips-lbl">{label}</span>}
        <span className="dr-chips-ico" aria-hidden="true">📅</span>
        {ranges.map((r) => (
          <button key={r.label} type="button"
            className={`dr-chip${r.days === value ? ' on' : ''}`}
            aria-pressed={r.days === value}
            onClick={() => onChange(r.days)}>
            {r.label}
          </button>
        ))}
      </div>
    );
  }

  const toggle = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 2, left: r.left + window.scrollX, width: r.width });
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)
          && btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <>
      <button ref={btnRef} type="button" className="dr-sel" onClick={toggle}>
        <span className="dr-sel-ico" aria-hidden="true">📅</span>
        <span className="dr-sel-lbl">{label ?? 'Date Range'}</span>
        <span className="dr-sel-val">
          {current?.label ?? 'All'}
          <i className="ti ti-chevron-down" />
        </span>
      </button>
      {open && createPortal(
        <div ref={popRef} className="dr-pop"
          style={{ top: pos.top, left: pos.left, minWidth: Math.max(90, pos.width) }}>
          {ranges.map((r) => (
            <div key={r.label}
              className={`dr-opt${r.days === value ? ' sel' : ''}`}
              onClick={() => { onChange(r.days); setOpen(false); }}>
              {r.label}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
